import { DayStatus, Decimal } from "@kase/my-marathon-communication";
import { isAfter, isSameDay, startOfDay } from "date-fns";
import {
    AppTime,
    Calendar,
    CalendarExternalEvent,
    TimeContract,
    TimeReport,
    Track,
} from "~/core";
import { getDayContract } from "~/core/slices/api/employee/utils";
import { dateFromApi, equalsApiDate } from "~/utils";
import { DayColor, DayStatsFilter } from "./interfaces";

const today = AppTime.signals.day;

export const getDayStats = (
    day: Date,
    contracts: TimeContract[],
    reports: TimeReport[],
    tracks: (Track | CalendarExternalEvent)[],
    calendars: Calendar[],
    filter: DayStatsFilter = {},
) => {
    const contract = getDayContract(contracts, day);
    const contracted = contract?.contracted_day
        ? new Decimal(contract.contracted_day).toNumber()
        : 0;
    const tracked =
        tracks
            .filter((i) => isSameDay(day, i.start))
            .reduce((acc, item) => acc + (item.stop - item.start), 0) /
        1000 /
        60 /
        60;
    let reportsFiltered = reports.filter((i) =>
        isSameDay(day, dateFromApi(i.date)),
    );

    if (filter.project) {
        reportsFiltered = reportsFiltered.filter(
            (i) => i.project.toString() === filter.project,
        );
    }

    if (filter.feecode) {
        reportsFiltered = reportsFiltered.filter(
            (i) =>
                i.line_type?.$case === "fee" &&
                i.line_type.fee.feecode?.toString() === filter.feecode,
        );
    }

    const reported = reportsFiltered.reduce(
        (acc, item) =>
            acc +
            (item.line_type?.$case === "fee"
                ? (item.line_type.fee.hours?.toNumber() ?? 0)
                : 0),
        0,
    );
    const isBeforeToday = isAfter(today.value, startOfDay(day));
    const calendar = calendars.find((i) => equalsApiDate(i.date, day));
    const isFinished = calendar?.status === DayStatus.Finished;
    const isYellow = calendar?.status === DayStatus.OnHold;
    const isRed =
        !isFinished && !isYellow && isBeforeToday && reported < contracted;
    const isGreen =
        !isRed &&
        !isYellow &&
        isBeforeToday &&
        (reported >= contracted || isFinished);
    const color: DayColor = isYellow
        ? "yellow"
        : isRed
          ? "red"
          : isGreen
            ? "green"
            : "gray";

    return {
        contracted,
        tracked,
        reported,
        color,
    };
};

// a lighter version, doesn't consider if the day is in the past or not
// used for the year overview
//
export const getDayStatsLight = (
    day: Date,
    contracts: TimeContract[],
    reports: TimeReport[],
    tracks: (Track | CalendarExternalEvent)[],
    calendars: Calendar[],
) => {
    const contract = getDayContract(contracts, day);
    const contracted = contract?.contracted_day
        ? new Decimal(contract.contracted_day).toNumber()
        : 0;
    const tracked =
        tracks
            .filter((i) => isSameDay(day, i.start))
            .reduce((acc, item) => acc + (item.stop - item.start), 0) /
        1000 /
        60 /
        60;
    const reported = reports
        .filter((i) => isSameDay(day, dateFromApi(i.date)))
        .reduce(
            (acc, item) =>
                acc +
                (item.line_type?.$case === "fee"
                    ? (item.line_type.fee.hours?.toNumber() ?? 0)
                    : 0),
            0,
        );
    const calendar = calendars.find((i) => equalsApiDate(i.date, day));
    const isFinished = calendar?.status === DayStatus.Finished;
    const isYellow = calendar?.status === DayStatus.OnHold;
    const isRed = !isFinished && !isYellow && reported < contracted;
    const isGreen =
        !isRed && !isYellow && (reported >= contracted || isFinished);
    const color: DayColor = isYellow
        ? "yellow"
        : isRed
          ? "red"
          : isGreen
            ? "green"
            : "gray";

    return {
        contracted,
        tracked,
        reported,
        color,
    };
};
